import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    OrderGroupId} from "@@/shared/order-groups/order-group-id";
import {
    orderGroupIdZodSchema,
} from "@@/shared/order-groups/order-group-id";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type { PaymentId} from "../payment-id";
import { paymentIdFactory, paymentIdZodSchema } from "../payment-id";
import type {
    PaymentProvider} from "../payment-providers";
import {
    paymentProviderZodSchema,
} from "../payment-providers";

type StripePaymentRefunded<ProviderData = unknown> = {
    _id: PaymentId;
    _type: "STRIPE_PAYMENT";
    _createdAt: IsoAndUnixTimestamp;
    _private: {
        paymentProviderData?: ProviderData;
        externalPaymentId: string;
        externalCustomerId?: string;
    };
    status: "REFUNDED";
    amount: number;
    amountRefunded: number;
    userId: UserId;
    paymentProvider: PaymentProvider;
    orderGroupId: OrderGroupId;
};

const stripePaymentRefundedFactory = <ProviderData = unknown>(
    params: Except<
        StripePaymentRefunded<ProviderData>,
        "_id" | "_type" | "status" | "_createdAt"
    >,
) => {
    const payment: StripePaymentRefunded<ProviderData> = {
        ...params,
        _id: paymentIdFactory(),
        _type: "STRIPE_PAYMENT",
        status: "REFUNDED",
        _createdAt: nowIsoAndUnix(),
    };

    return payment;
};

const stripePaymentRefundedZodSchema: z.ZodSchema<StripePaymentRefunded> =
    z.object({
        _id: paymentIdZodSchema,
        _createdAt: isoAndUnixZodSchema,
        _type: z.literal("STRIPE_PAYMENT"),
        _private: z.object({
            paymentProviderData: z.any(),
            externalPaymentId: z.string(),
            externalCustomerId: z.string(),
        }),
        status: z.literal("REFUNDED"),
        amount: z.number().min(0),
        amountRefunded: z.number().min(0),
        userId: userIdZodSchema,
        paymentProvider: paymentProviderZodSchema,
        orderGroupId: orderGroupIdZodSchema,
    });

export { stripePaymentRefundedFactory, stripePaymentRefundedZodSchema };
export type { StripePaymentRefunded };
